import { Box, Typography } from '@mui/material';
import { object, string } from 'prop-types';
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router';
import isYoutube from 'utils/isYoutube';
import isSharedDocumentsWebUrl from 'utils/isSharedDocumentsWebUrl';
import ContentTracking from 'services/api/ContentTracking';
import { LINK_PREVIEW_MIN_WIDTH_PX } from '../messageBubbleMaxWidth';
import Image from './Image';
import YoutubeIframe from './YoutubeIFrame';

const LinkPreview = ({ preview, messageId }) => {
  const navigate = useNavigate();
  const handleClick = useCallback(
    event => {
      if (isSharedDocumentsWebUrl(preview.link)) {
        const modifiedClick =
          event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
        if (!modifiedClick) {
          event.preventDefault();
          try {
            const u = new URL(preview.link, window.location.href);
            navigate(`${u.pathname}${u.search}${u.hash}`);
          } catch {
            navigate(preview.link);
          }
        }
      }
      if (messageId) {
        ContentTracking.trackLinkClick({ messageId, url: preview.link });
      }
    },
    [messageId, navigate, preview.link],
  );

  const hasYoutubeEmbed = isYoutube(preview.link) && preview.link.split('v=')[1];
  const hasImage = preview.image?.startsWith('https://');
  const hasText = Boolean(preview.title || preview.description);
  if (!hasText && !hasYoutubeEmbed && !hasImage) {
    return null;
  }

  return (
    <Box
      component="a"
      href={preview.link}
      rel="noopener noreferrer"
      target="_blank"
      onClick={handleClick}
      sx={{
        textDecoration: 'none !important',
        display: 'block',
        minWidth: LINK_PREVIEW_MIN_WIDTH_PX,
        maxWidth: '100%',
        containerType: 'inline-size',
        '&:hover, &:active, &:visited, &:focus': {
          textDecoration: 'none !important',
        },
      }}
    >
      {hasYoutubeEmbed ? <YoutubeIframe url={preview.link} /> : <Image src={preview.image} />}
      <Box sx={{ padding: '15px', backgroundColor: 'rgba(0, 0, 0, 0.05)' }}>
        <Typography sx={{ paddingBottom: '5px', fontWeight: 'bold' }}>{preview.title}</Typography>
        <Typography variant="caption" sx={{ color: 'secondary.main' }}>
          {preview.description}
        </Typography>
      </Box>
    </Box>
  );
};

LinkPreview.propTypes = {
  preview: object.isRequired,
  messageId: string,
};

export default LinkPreview;
